/* Biome cart drawer — optimistic UI over a desired-state sync engine.
 *
 * Architecture (the "why" of the rewrite):
 *   The UI renders INSTANTLY from local intent; Shopify is reconciled in the
 *   background and only consulted as the source of truth, never as a gate.
 *
 *   - Desired state is ABSOLUTE: for the five known SKUs we track "the shopper
 *     wants N of variant X" (not deltas), keyed by a stable alias
 *     (vanilla/chocolate/metab/bloat/bundle) — never by Shopify line keys,
 *     which churn whenever properties/discounts change and which caused the
 *     old queue to poison itself with synthetic "pending:" keys (a 422 on one
 *     bad key fails the WHOLE update, and the queue resent it forever — the
 *     "cart gets stuck" bug).
 *   - All known-SKU writes go through POST /cart/update.js keyed by VARIANT id:
 *     per Shopify docs, update.js adds the line if the variant isn't in the
 *     cart and updates the first matching line if it is, takes `attributes`
 *     in the same request, and does NOT validate inventory. One endpoint,
 *     absolute quantities, idempotent — safe to coalesce and to retry.
 *   - ONE request in flight at a time, bursts coalesced (debounce with a
 *     max-wait so a click storm can't postpone the first write forever).
 *   - Convergence rule: after a write, any desire the shopper hasn't re-touched
 *     mid-flight is dropped and the server's answer adopted — even if it
 *     differs (e.g. an app capped the qty). Desires can never loop.
 *   - Errors: 429/5xx/network → exponential backoff, desires kept. Other 4xx →
 *     refetch truth, retry once, then adopt server. Nothing is ever stuck.
 *   - Checkout / page exit: the checkout button drains the queue before
 *     navigating; pagehide fires a keepalive flush as a belt-and-braces.
 *
 * CartSyncEngine is DOM-free (exposed on globalThis for the Node test
 * harness); the drawer wiring below it owns all rendering.
 */
(function () {
  'use strict';

  // ── Desired-state sync engine (DOM-free) ────────────────────────────────────
  function CartSyncEngine(opts) {
    var V = opts.variants || {};           // alias -> variant id
    var routes = opts.routes;              // {update, cart, add, change}
    var fetchImpl = opts.fetchImpl || function () { return window.fetch.apply(window, arguments); };
    var onChange = opts.onChange || function () {};
    var planFor = opts.planFor || function () { return null; };  // alias -> desired selling plan id (null = one-time)
    var setTimer = opts.setTimer || function (fn, ms) { return setTimeout(fn, ms); };
    var clearTimer = opts.clearTimer || function (id) { clearTimeout(id); };

    var self = {
      cart: null,            // last server truth (never mutated optimistically)
      syncing: false,
      lastSyncAt: 0,
      collapseDisabled: false
    };
    var wantVar = {};        // alias -> absolute qty the shopper wants
    var wantOthers = {};     // line key -> absolute qty (foreign lines only)
    var wantAttrs = {};      // cart attribute -> value
    var addQueue = [];       // foreign add.js items (safety net; known SKUs never use this)
    var needsGet = false;    // an add.js completed; fetch full cart to reconcile
    var flushTimer = null, firstScheduledAt = 0, failStreak = 0, collapseTries = 0;
    var planFixSkip = {};    // 'alias>planId' latches for plan rewrites the platform keeps bouncing
    var planFixTries = {};   // per-target failure counts (the global failStreak resets on reads)
    var addTries = {};       // per-alias SEMANTIC add failures (transient faults retry forever)
    var drainWaiters = [];

    function aliasByVid(vid) {
      for (var a in V) { if (V[a] && V[a] === vid) return a; }
      return null;
    }
    function serverLines(alias) {
      var vid = V[alias], out = [];
      if (!vid || !self.cart) return out;
      (self.cart.items || []).forEach(function (it) { if (it.id === vid) out.push(it); });
      return out;
    }
    function serverQty(alias) {
      return serverLines(alias).reduce(function (s, l) { return s + l.quantity; }, 0);
    }
    function displayQty(alias) {
      return Object.prototype.hasOwnProperty.call(wantVar, alias) ? wantVar[alias] : serverQty(alias);
    }
    function primaryKey(alias) { var ls = serverLines(alias); return ls.length ? ls[0].key : null; }
    function planOf(line) {
      var spa = line && line.selling_plan_allocation;
      return (spa && spa.selling_plan && spa.selling_plan.id) || null;
    }
    // First line whose selling plan differs from the desired plan for its alias
    // (sub line that should be one-time, wrong cadence, or one-time that should
    // subscribe). Fixed one per flush cycle via /cart/change.js.
    function planDriftFirst() {
      for (var a in V) {
        if (!V[a]) continue;
        var ls = serverLines(a);
        if (!ls.length) continue;
        var target = planFor(a) || null;
        if (planFixSkip[a + '>' + (target || 'none')]) continue;
        if (Number(planOf(ls[0]) || 0) !== Number(target || 0)) return { alias: a, line: ls[0], target: target };
      }
      return null;
    }
    // Aliases the shopper wants in the cart that have no server line yet —
    // these must go through add.js (update.js can't attach a selling plan).
    function newLineItems() {
      var out = [];
      for (var a in wantVar) {
        if (!V[a]) continue;
        if (wantVar[a] > 0 && serverLines(a).length === 0) out.push({ alias: a, id: V[a], quantity: wantVar[a] });
      }
      return out;
    }
    function otherLines() {
      var known = {};
      for (var a in V) { if (V[a]) known[V[a]] = true; }
      var out = [];
      ((self.cart && self.cart.items) || []).forEach(function (it) {
        if (known[it.id]) return;
        var q = Object.prototype.hasOwnProperty.call(wantOthers, it.key) ? wantOthers[it.key] : it.quantity;
        if (q > 0) out.push({ line: it, q: q });
      });
      return out;
    }
    function attrValue(name) {
      var attrs = (self.cart && self.cart.attributes) || {};
      return attrs[name];
    }
    function dupesExist() {
      if (self.collapseDisabled) return false;
      for (var a in V) { if (V[a] && serverLines(a).length > 1) return true; }
      return false;
    }
    function hasPending() {
      for (var k in wantVar) return true;
      for (var o in wantOthers) return true;
      for (var t in wantAttrs) return true;
      if (addQueue.length || needsGet) return true;
      if (planDriftFirst()) return true;
      return dupesExist();
    }
    function resolveDrains() {
      if (self.syncing || hasPending()) return;
      var ws = drainWaiters; drainWaiters = [];
      ws.forEach(function (w) { w(); });
    }

    // Debounced flush with a max-wait: bursts coalesce into one request, but a
    // sustained click storm still writes within ~600ms of the first click.
    function flushSoon(ms) {
      var delay = ms == null ? 180 : ms;
      var now = Date.now();
      if (!firstScheduledAt) firstScheduledAt = now;
      if (now - firstScheduledAt > 600) delay = 0;
      if (flushTimer) clearTimer(flushTimer);
      flushTimer = setTimer(flush, delay);
    }
    function backoffMs() {
      return Math.min(4000, 300 * Math.pow(2, Math.max(0, failStreak - 1))) + Math.floor(Math.random() * 150);
    }

    // Diff desires against server truth. Entries already true on the server are
    // confirmed locally (no network). Returns null when nothing needs sending.
    function buildPayload() {
      var updates = {}, attrs = null, sentVar = {}, sentOthers = {}, sentAttrs = {}, dirty = false;
      for (var a in wantVar) {
        if (!V[a]) { delete wantVar[a]; continue; }
        var q = wantVar[a];
        var lines = serverLines(a);
        if (!lines.length) {                            // additions ride add.js (selling plans)
          if (q <= 0) delete wantVar[a];                // removing what isn't there: done
          continue;
        }
        if (q === serverQty(a) && lines.length <= 1) { delete wantVar[a]; continue; }
        sentVar[a] = q; dirty = true;
        updates[lines[0].key] = q;                      // pin to the primary line by key…
        for (var i = 1; i < lines.length; i++) updates[lines[i].key] = 0; // …and fold dupes
      }
      if (!self.collapseDisabled) {
        for (var ca in V) {                             // pure collapse (no desire pending)
          if (Object.prototype.hasOwnProperty.call(wantVar, ca)) continue;
          var ls = serverLines(ca);
          if (ls.length > 1) {
            var total = ls.reduce(function (s, l) { return s + l.quantity; }, 0);
            updates[ls[0].key] = total;
            for (var j = 1; j < ls.length; j++) updates[ls[j].key] = 0;
            dirty = true;
          }
        }
      }
      for (var k in wantOthers) {
        var exists = ((self.cart && self.cart.items) || []).some(function (it) { return it.key === k; });
        if (!exists) { delete wantOthers[k]; continue; }  // line vanished (other tab) — drop
        sentOthers[k] = wantOthers[k]; updates[k] = wantOthers[k]; dirty = true;
      }
      for (var at in wantAttrs) {
        if (String(attrValue(at)) === String(wantAttrs[at])) { delete wantAttrs[at]; continue; }
        (attrs = attrs || {})[at] = wantAttrs[at]; sentAttrs[at] = wantAttrs[at]; dirty = true;
      }
      if (!dirty) return null;
      var body = {};
      var hasU = false; for (var u in updates) { hasU = true; break; }
      if (hasU) body.updates = updates;
      if (attrs) body.attributes = attrs;
      if (!hasU && !attrs) return null;
      return { body: body, sentVar: sentVar, sentOthers: sentOthers, sentAttrs: sentAttrs, hadDupes: dupesExist() };
    }

    function confirmSnapshot(snap) {
      // Adopt-server rule: anything not re-touched mid-flight is settled by the
      // server's reply, whatever it says. Guarantees convergence (no resend loops).
      for (var a in snap.sentVar) { if (wantVar[a] === snap.sentVar[a]) delete wantVar[a]; }
      for (var k in snap.sentOthers) { if (wantOthers[k] === snap.sentOthers[k]) delete wantOthers[k]; }
      for (var t in snap.sentAttrs) { if (String(wantAttrs[t]) === String(snap.sentAttrs[t])) delete wantAttrs[t]; }
    }

    function postJSON(url, body, keepalive) {
      var init = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      };
      if (keepalive) init.keepalive = true;
      return fetchImpl(url, init);
    }

    function flush() {
      if (flushTimer) { clearTimer(flushTimer); flushTimer = null; }
      firstScheduledAt = 0;
      if (self.syncing) return;                 // completion handler re-drains
      if (!hasPending()) { resolveDrains(); return; }

      // Foreign adds first (rare safety net) — then a GET reconciles the new keys.
      if (addQueue.length) {
        var items = addQueue.slice();
        self.syncing = true;
        postJSON(routes.add + '.js', { items: items })
          .then(function (r) {
            self.syncing = false;
            if (r && r.ok) { addQueue.splice(0, items.length); needsGet = true; failStreak = 0; flush(); return; }
            failStreak++;
            if (failStreak >= 3) { addQueue.length = 0; needsGet = true; failStreak = 0; } // surrender, resync
            flushSoon(backoffMs());
          })
          .catch(function () { self.syncing = false; failStreak++; flushSoon(backoffMs()); });
        return;
      }
      if (needsGet) {
        self.syncing = true;
        fetchImpl(routes.cart + '.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (c) {
            self.syncing = false;
            if (c) { needsGet = false; failStreak = 0; adopt(c); }
            else { failStreak++; }
            if (hasPending()) flushSoon(c ? 0 : backoffMs()); else resolveDrains();
          })
          .catch(function () { self.syncing = false; failStreak++; flushSoon(backoffMs()); });
        return;
      }

      // New lines for known SKUs — add.js so the line carries its selling plan
      // (one-time lines simply omit it). ONE alias per cycle: add.js rejects the
      // whole request if any item fails, so batching would let one sold-out SKU
      // block the rest. Absolute desire stays queued until the reconcile GET
      // confirms it, so a mid-flight click still converges.
      var newItems = newLineItems();
      if (newItems.length) {
        var nu = newItems[0];
        var sentNewQty = nu.quantity;
        var item = { id: nu.id, quantity: nu.quantity };
        var np = planFor(nu.alias);
        if (np) item.selling_plan = np;
        self.syncing = true;
        postJSON(routes.add + '.js', { items: [item] })
          .then(function (r) {
            self.syncing = false;
            if (r && r.ok) { needsGet = true; failStreak = 0; delete addTries[nu.alias]; flush(); return; }
            failStreak++;
            if (r.status === 429 || r.status >= 500) { flushSoon(backoffMs()); return; }  // transient: retry forever
            // Semantic 4xx (sold out / unpublished): retry once, then surrender
            // this add and resync — never burn a desire on a transient blip.
            addTries[nu.alias] = (addTries[nu.alias] || 0) + 1;
            if (addTries[nu.alias] >= 2) {
              if (wantVar[nu.alias] === sentNewQty) delete wantVar[nu.alias];
              delete addTries[nu.alias];
              failStreak = 0; needsGet = true;
            }
            flushSoon(backoffMs());
          })
          .catch(function () { self.syncing = false; failStreak++; flushSoon(backoffMs()); });
        return;
      }

      // One plan rewrite per cycle: change.js swaps the line's selling plan
      // (or strips it with null) and sets the absolute qty in the same call.
      var drift = planDriftFirst();
      if (drift) {
        var dq = Object.prototype.hasOwnProperty.call(wantVar, drift.alias) ? wantVar[drift.alias] : serverQty(drift.alias);
        if (dq > 0) {
          var skipKey = drift.alias + '>' + (drift.target || 'none');
          self.syncing = true;
          postJSON(routes.change + '.js', { id: drift.line.key, quantity: dq, selling_plan: drift.target })
            .then(function (r) {
              if (r.ok) return r.json().then(function (c) { return { ok: true, cart: c }; });
              return { ok: false, status: r.status };
            })
            .then(function (res) {
              self.syncing = false;
              if (res.ok && res.cart && res.cart.items !== undefined) {
                failStreak = 0;
                delete planFixTries[skipKey];
                if (wantVar[drift.alias] === dq) delete wantVar[drift.alias];
                adopt(res.cart);
                if (hasPending()) flush(); else resolveDrains();
                return;
              }
              failStreak++;
              if (res.status === 429 || res.status >= 500) { flushSoon(backoffMs()); return; }
              // semantic 4xx (key churned, line gone, app rejection): resync; on
              // repeat, stop fighting this particular rewrite for the session.
              planFixTries[skipKey] = (planFixTries[skipKey] || 0) + 1;
              if (planFixTries[skipKey] >= 2) { planFixSkip[skipKey] = true; }
              needsGet = true;
              flushSoon(backoffMs());
            })
            .catch(function () { self.syncing = false; failStreak++; flushSoon(backoffMs()); });
          return;
        }
      }

      var snap = buildPayload();
      if (!snap) { onChange(); resolveDrains(); return; }   // everything confirmed locally
      self.syncing = true;
      postJSON(routes.update + '.js', snap.body)
        .then(function (r) {
          if (r.ok) return r.json().then(function (c) { return { ok: true, cart: c }; });
          return { ok: false, status: r.status };
        })
        .then(function (res) {
          self.syncing = false;
          if (res.ok && res.cart && res.cart.items !== undefined) {
            failStreak = 0;
            confirmSnapshot(snap);
            adopt(res.cart);
            // If a 200 response keeps the duplicate lines we just folded (an app
            // re-splitting them), stop collapsing this session rather than looping.
            if (snap.hadDupes) {
              collapseTries = dupesExist() ? collapseTries + 1 : 0;
              if (collapseTries >= 3) self.collapseDisabled = true;
            }
            if (hasPending()) flush(); else resolveDrains();
            return;
          }
          failStreak++;
          if (res.status === 429 || res.status >= 500) {     // transient: keep desires, back off
            flushSoon(backoffMs());
            return;
          }
          // Semantic 4xx (bad key after external change, unpublished variant, …):
          // refetch truth; after a second consecutive failure adopt the server
          // for everything we tried to send so the queue can never wedge.
          refresh().then(function () {
            if (failStreak >= 2) { confirmSnapshot(snap); failStreak = 0; }
            if (hasPending()) flushSoon(backoffMs()); else resolveDrains();
          });
        })
        .catch(function () {
          self.syncing = false;
          failStreak++;
          if (failStreak >= 4) { failStreak = 0; refresh(); }  // resync display; desires survive
          flushSoon(backoffMs());
        });
    }

    function adopt(c) {
      self.cart = c;
      self.lastSyncAt = Date.now();
      // If duplicate-line collapses are repeatedly bounced by the platform,
      // stop trying for this session rather than looping.
      if (dupesExist() && failStreak >= 2) self.collapseDisabled = true;
      // Legacy carts can arrive with property-split duplicate lines or plans
      // that no longer match the chosen cadence; fold/fix them in the
      // background (a plain refresh never schedules a flush on its own).
      if (!self.syncing) { if (dupesExist() || planDriftFirst()) flushSoon(); }
      onChange();
    }

    function refresh() {
      if (self.syncing) return Promise.resolve(null);  // an in-flight write returns fresher truth
      return fetchImpl(routes.cart + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (c) { if (c && c.items !== undefined) adopt(c); return c; })
        .catch(function () { return null; });
    }

    function drain(timeoutMs) {
      if (!self.syncing && !hasPending()) return Promise.resolve(true);
      flushSoon(0);
      return new Promise(function (resolve) {
        var done = false;
        var t = setTimer(function () { if (!done) { done = true; resolve(false); } }, timeoutMs || 8000);
        drainWaiters.push(function () { if (!done) { done = true; clearTimer(t); resolve(true); } });
      });
    }

    // pagehide insurance: replay the outstanding diff with keepalive so a quick
    // exit (link tap, tab close) doesn't lose the last click. Absolute updates
    // make a double-apply harmless.
    function flushKeepalive() {
      if (!hasPending()) return;
      var snap = buildPayload();
      if (!snap) return;
      try { postJSON(routes.update + '.js', snap.body, true).catch(function () {}); } catch (e) { /* ignore */ }
    }

    self.aliasByVid = aliasByVid;
    self.serverLines = serverLines;
    self.serverQty = serverQty;
    self.displayQty = displayQty;
    self.primaryKey = primaryKey;
    self.planOfLine = planOf;
    self.otherLines = otherLines;
    self.attrValue = attrValue;
    self.hasPending = hasPending;
    self.setVar = function (alias, qty) { wantVar[alias] = Math.max(0, qty | 0); flushSoon(); onChange(); };
    self.bumpVar = function (alias, d) { self.setVar(alias, displayQty(alias) + d); };
    self.setOther = function (key, qty) { wantOthers[key] = Math.max(0, qty | 0); flushSoon(); onChange(); };
    self.setAttr = function (name, val) {
      if (String(attrValue(name)) === String(val) && !Object.prototype.hasOwnProperty.call(wantAttrs, name)) return;
      wantAttrs[name] = String(val); flushSoon();
    };
    self.queueAdd = function (item) { addQueue.push(item); flushSoon(0); };
    self.flush = flush;
    self.flushSoon = flushSoon;
    self.drain = drain;
    self.refresh = refresh;
    self.flushKeepalive = flushKeepalive;
    return self;
  }
  try { (typeof globalThis !== 'undefined' ? globalThis : window).BiomeCartEngine = CartSyncEngine; } catch (e) { /* ignore */ }

  // ── Drawer wiring (DOM) ──────────────────────────────────────────────────────
  if (typeof document === 'undefined') return;
  var el = document.querySelector('[data-cart-config]');
  if (!el) return;
  var cfg = JSON.parse(el.textContent);
  var V = cfg.variants;
  // The two always-free bonus items ride the same engine as managed aliases —
  // they're real $0 variants, auto-added/removed by rule, never shown as lines.
  var GIFTS = cfg.gifts || {};
  if (GIFTS.challenge) V.g_challenge = GIFTS.challenge;
  if (GIFTS.guide) V.g_guide = GIFTS.guide;
  var GIFT_ALIASES = ['g_challenge', 'g_guide'].filter(function (g) { return !!V[g]; });
  // alias -> {weeks: appstle selling plan id}; vanilla/chocolate share the shake product's plans,
  // and the two All-In-One Bundle flavors (bundle = Chocolate, bundle_van = Vanilla Caramel)
  // share the bundle product's — so both flavors always land on ONE plan + cadence.
  var PLANS = cfg.plans || {};
  var PLANMAP = {
    vanilla: PLANS.shake || {}, chocolate: PLANS.shake || {},
    metab: PLANS.metab || {}, bloat: PLANS.bloat || {},
    bundle: PLANS.bundle || {}, bundle_van: PLANS.bundle || {}
  };
  // Both bundle flavors are one product: one tier, one upsell slot, one image.
  var BUNDLE_ALIASES = ['bundle', 'bundle_van'];
  function isBundleAlias(a){ return a === 'bundle' || a === 'bundle_van'; }
  var BUNDLE_FLAVOR = cfg.bundleFlavors || {};
  if (cfg.img && !cfg.img.bundle_van) cfg.img.bundle_van = cfg.img.bundle;
  var ONCE = [cfg.base, cfg.base - cfg.vol2, cfg.base - cfg.vol3];
  var SUB  = [cfg.base - cfg.subDiscount, cfg.base - cfg.vol2 - cfg.subDiscount, cfg.base - cfg.vol3 - cfg.subDiscount];
  var MEALS = cfg.mealsPerBox, FREE = cfg.freeShipMin;
  var PILL_SUB = [56,46,36,26], PILL_ONCE = [66,56,46,36];
  // Bundle per-unit ladder at qty 1 / 2 / 3+ — mirrors the live 💵 volume rules
  // (−$29.50@2, −$50@3+ one-time; −$24.50@2, −$36@3+ sub) on the $149 variant
  // with its −$30 Appstle plan. This IS the AIO page's tier pricing; checkout
  // reproduces it exactly.
  var BUNDLE_ONCE = [149, 119.5, 99], BUNDLE_SUB = [119, 94.5, 83];
  function bundleTier(q){ if(q>=3) return 2; if(q>=2) return 1; return 0; }
  function bundleUnit(plan, q){ return (plan==='sub'?BUNDLE_SUB:BUNDLE_ONCE)[bundleTier(Math.max(1,q||1))]; }
  // The bundle's volume tier counts the WHOLE product — Shopify's 💵 BUY 2+/3+ rules
  // do — so it's both flavor aliases together, never one alias's own qty (that drew
  // 1 Chocolate + 1 Vanilla on subscribe at $213.50 against the $189 checkout bills).
  function bundleQ(m){ return m.bundle.q + m.bundle_van.q; }
  // Upsell slots still open. The bundle is ONE slot whichever flavor is in the cart —
  // a Vanilla bundle in the cart must not leave an "add the bundle" card showing.
  function upsellSlotsFree(m){
    return ['metab','bloat','bundle'].filter(function(id){ return id==='bundle' ? !bundleQ(m) : !m[id].q; });
  }
  // Safety net for a bundle flavor with NO alias (e.g. a future third flavor): such a
  // line is "foreign" — drawn from Shopify's data — but still counts toward the same
  // tier. Only lines on the shopper's purchase type count, because those rules are
  // subscription-only or one-time-only (a one-time line never lifts a subscribed
  // line's tier at checkout, so it mustn't here either). Display only.
  function bundleSiblingQty(m){
    var pid = cfg.bundleProductId; if(!pid) return 0;
    var wantSub = state.plan === 'sub';
    return m.others.reduce(function(s, o){
      var l = o.line;
      return (l && l.product_id === pid && !!l.selling_plan_allocation === wantSub) ? s + o.q : s;
    }, 0);
  }

  var drawer = document.querySelector('[data-cart-drawer]');
  var back = document.querySelector('[data-cart-back]');
  var content = document.querySelector('[data-cart-content]');
  // Optional full-page cart surface (sections/biome-cart.liquid). When present, the
  // SAME engine + pricing renders the two-column page in addition to the drawer. The
  // drawer always ships on commerce templates (via biome-header), so the guard below
  // still requires it; everything page-related here is purely additive.
  var page = document.querySelector('[data-cart-page]');
  var pageCount = document.querySelector('[data-cart-page-count]');
  var pageHead = document.querySelector('.cartpg__head'); // page header (title + continue) — hidden when the cart is empty

  // Shopify-rendered express checkout buttons (content_for_additional_checkout_buttons),
  // parked in Liquid; relocated into the summary card's slot on each render (see park/mount).
  var expressHome = document.querySelector('[data-cart-express-home]');
  var lastSigPage = '', lastMsPage = '';
  if (!drawer || !content) return;

  // freqManual: the shopper picked a cadence by hand — the auto-snap stands
  // down until the next quantity change, which re-enables it (per design).
  var state = { plan: 'sub', freq: 4, freqManual: false };

  // Desired Appstle selling plan for an alias under the current plan/frequency.
  // One-time mode and the $0 gift lines get none.
  function planFor(alias){
    if(state.plan !== 'sub') return null;
    var m = PLANMAP[alias];
    if(!m) return null;
    return m[String(state.freq)] || null;
  }

  // Any full-price line in the cart? Judged on ORIGINAL price so a legit paid line
  // discounted to $0 by a code still counts as paid. The five known aliases are
  // always paid products; the gift aliases and $0 foreign lines never are.
  function hasPaidLine(m){
    if(m.vanilla.q + m.chocolate.q + m.metab.q + m.bloat.q + bundleQ(m) > 0){ return true; }
    for(var i=0;i<m.others.length;i++){ if(m.others[i].line.original_line_price > 0){ return true; } }
    return false;
  }
  // Only freebies left — $0 lines / bonus aliases and nothing paid?
  function cartAllFree(m){
    if(hasPaidLine(m)){ return false; }
    var giftQty = GIFT_ALIASES.reduce(function(s,g){ return s + engine.displayQty(g); }, 0);
    return giftQty + m.others.length > 0;
  }

  // Should the checkout button refuse this cart? Only a NON-funnel all-free cart.
  // Bundle carts (cart-bridge funnels) are curated upstream and already protected
  // by the wipe-on-removal rule — the fence must not second-guess them, so a
  // funnel whose anchor is voucher-discounted (original price still > $0) or even
  // a hypothetical $0-priced pay-shipping-only anchor always reaches checkout.
  function blocksCheckout(m){
    return !isBundleCart() && cartAllFree(m);
  }

  // Has the cart earned the two free bonuses? 2+ shake boxes, or 3+ All-In-One
  // Bundles (either flavor) — the Full Body Transformation tier the AIO PDP and
  // /pages/aio-bundles advertise "2 free gifts" on. Drives the rule AND the bonus row.
  function bonusUnlocked(m){
    if(m.vanilla.q + m.chocolate.q >= cfg.bonusThreshold){ return true; }
    var bmin = cfg.bundleBonusThreshold || 0;
    return bmin > 0 && bundleQ(m) >= bmin;
  }

  // On-site free-bonus rule (NON-bundle carts only): once bonusUnlocked, the two $0
  // digital bonuses become real cart lines so fulfilment ships them; below that
  // they're removed — but only when shakes / AIO bundles are the cart's only paid
  // context. The pill bundle pages add these same variants as their own gifts, so
  // with other paid lines present the rule must not strip gifts it didn't add.
  // The bonus row is their UI — they never render as lines.
  function applyGiftRule(m){
    var bundlesQualify = (cfg.bundleBonusThreshold || 0) > 0;
    var otherPaid = m.metab.q + m.bloat.q + (bundlesQualify ? 0 : bundleQ(m)) > 0;
    for(var i=0;i<m.others.length && !otherPaid;i++){ if(m.others[i].line.original_line_price > 0){ otherPaid = true; } }
    var want;
    if(bonusUnlocked(m)){ want = 1; }
    else if(otherPaid){ return; }
    else { want = 0; }
    GIFT_ALIASES.forEach(function(g){
      if(engine.displayQty(g) !== want){ engine.setVar(g, want); }
    });
  }

  // Freebies never stand alone: when nothing paid remains (the shopper deleted the
  // paid lines but kept the $0 extras — e.g. a bundle page's gift bottles), sweep
  // every remaining $0 line. NB the real fence has to be server-side (a $0 order
  // can be minted straight from a /cart/VARIANT:1 permalink without the theme ever
  // running) — this only keeps on-site carts honest.
  function stripOrphanFreebies(m){
    if(!cartAllFree(m)){ return; }
    GIFT_ALIASES.forEach(function(g){ if(engine.displayQty(g) > 0){ engine.setVar(g, 0); } });
    m.others.forEach(function(o){ if(o.line.original_line_price === 0 && o.q > 0){ engine.setOther(o.line.key, 0); } });
  }

  // ── Bundle lock (cart-bridge funnels) ───────────────────────────────────────
  // A cart seeded by /pages/cart-bridge carries a `_bundle` cart attribute, set by
  // the bridge server-side. It's an all-or-nothing funnel pack: the shopper may ADD
  // upsells, but removing or reducing ANY line wipes the whole cart (owner decision
  // 2026-06-24). Detection rides that cart attribute — durable across checkout →
  // back → reloads and immune to the per-browser / cart-token snapshot races the
  // old localStorage lock suffered (those let it silently strip the gift PDFs, or
  // disengage entirely and leave a $0 free-items checkout). The removal is caught at
  // click time (see onCartClick) so engine-internal line churn never trips it.
  function isBundleCart(){ return engine.attrValue('_bundle') === '1'; }
  var bundleDestroying = false;
  function destroyBundleCart(){
    if(bundleDestroying){ return; }
    bundleDestroying = true;
    // Drop the marker, empty the cart, then re-pull truth so the UI shows it gone.
    fetch('/cart/update.js', { method:'POST', credentials:'include', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ attributes: { _bundle: '' } }) })
      .then(function(){ return fetch('/cart/clear.js', { method:'POST', credentials:'include' }); })
      .then(function(){ return engine.refresh(); })
      .catch(function(){ window.location.href = '/cart'; })
      .then(function(){ bundleDestroying = false; });
  }

  // Route each cart change to the right freebie rule. A bundle cart owns ALL its
  // freebies (incl. the 15-Day Challenge / Nutrition Guide — the SAME variants the
  // on-site bonus uses), so the shake-based applyGiftRule is suspended for it (it
  // would otherwise strip those gifts at 0 shakes); every other cart uses the rule.
  function applyCartRules(m){
    if(isBundleCart()){ return; }
    applyGiftRule(m);
    stripOrphanFreebies(m);
  }

  // Returning shoppers: pick the plan/frequency back up from the cart attributes
  // the last session persisted (falling back to a recognizable Appstle plan on a
  // line, e.g. a cart built by a bundle page). Runs once, on the first server
  // adoption, INSTEAD of the cadence auto-snap (which would overwrite a manually-
  // picked frequency).
  var hydrated = false;
  // addAndOpen paint batching (see onChange + addAndOpen): >0 while its items go in.
  var paintBatch = 0, paintOwed = false;
  function hydrateFromAttrs(){
    var p = engine.attrValue('plan');
    if(p==='sub'||p==='once'){ state.plan = p; }
    var w = Number(engine.attrValue('delivery_weeks'));
    if([2,4,6,8,10,12].indexOf(w)>=0){
      state.freq = w;
      // A persisted cadence that differs from the supply snap was a manual
      // pick last session — keep honoring it until the qty changes.
      var tgt = snapTargetFreq(model());
      if(tgt && tgt !== w){ state.freqManual = true; }
      return;
    }
    if(p){ return; }
    // No attributes — infer from the first subscribed line's Appstle plan.
    var aliases = ['vanilla','chocolate','metab','bloat','bundle','bundle_van'];
    for(var i=0;i<aliases.length;i++){
      var ls = engine.serverLines(aliases[i]);
      if(!ls.length) continue;
      var pid = engine.planOfLine(ls[0]);
      if(!pid) continue;
      var map = PLANMAP[aliases[i]] || {};
      for(var wk in map){
        if(Number(map[wk]) === Number(pid)){ state.plan='sub'; state.freq=Number(wk); return; }
      }
    }
  }

  var engine = CartSyncEngine({
    variants: V,
    routes: cfg.routes,
    planFor: planFor,
    onChange: function () {
      if(!hydrated && engine.cart){
        hydrated = true;
        // Skip if the shopper started interacting before the boot fetch landed —
        // their live choices beat last session's persisted attributes.
        if(!engine.hasPending()){ hydrateFromAttrs(); applyCartRules(model()); render(); if(page) renderPage(); return; }
      }
      var m = model();
      autoSnapFreq(m);
      applyCartRules(m);
      // Keep the cart attributes truthful whenever there's anything in the cart —
      // orders carry plan/delivery_weeks even if the shopper never touched the toggle.
      if(m.vanilla.q + m.chocolate.q + m.metab.q + m.bloat.q + bundleQ(m) > 0){
        engine.setAttr('plan', state.plan);
        if(state.plan === 'sub'){ engine.setAttr('delivery_weeks', String(state.freq)); }
      }
      // Inside addAndOpen the state work above still runs per item (the cadence snap is
      // path-dependent, so it must), but painting waits for addAndOpen's single render.
      if(paintBatch){ paintOwed = true; return; }
      repaint();
      if(page) repaintPage();
    }
  });

  function fmt(n){ var v = Math.round(Number(n)*100)/100; return Number.isInteger(v) ? String(v) : v.toFixed(2); }
  function tierIndex(tb){ return tb>=3?2:(tb>=2?1:0); }
  function pillTier(q){ if(q<=1)return 0; if(q===2)return 1; if(q<=5)return 2; return 3; }
  function upsellPrice(id, plan, hasShakes, pillQty){
    if(id==='metab'||id==='bloat'){
      var ladder = (plan==='sub'?PILL_SUB:PILL_ONCE)[pillTier(Math.max(1,pillQty||1))];
      // Member price with shakes in the cart — but never worse than the volume
      // ladder (6+ pills keep their 26/36), mirroring the pricing function.
      if(hasShakes){ return Math.min(plan==='sub'?31:34, ladder); }
      return ladder;
    }
    if(id==='bundle') return bundleUnit(plan, 1);
    return 0;
  }
  // Extra savings from switching a one-time cart to subscription (shakes + added upsells at sub pricing).
  function switchSavOf(t){
    if(state.plan==='sub') return 0;
    var ss = SUB[t.tier]*t.tb;
    // Subscribe re-plans BOTH bundle flavors, so price them at their combined tier.
    var bq = t.ups.reduce(function(s,u){ return s + (isBundleAlias(u.id) ? u.q : 0); }, 0);
    var su = t.ups.reduce(function(s,u){
      var unit = isBundleAlias(u.id) ? bundleUnit('sub', bq) : upsellPrice(u.id,'sub',t.hasShakes,t.pillQty);
      return s + unit*u.q;
    },0);
    return Math.max(0, t.grandSub - (ss+su));
  }
  function switchSavText(sv){ return sv>0 ? ' · save $'+fmt(sv)+' on this order' : ''; }

  // Render model: server truth + the shopper's pending intent, summed per alias
  // (duplicate lines for the same variant display as one). All controls for
  // known SKUs are keyed by ALIAS — line keys never reach the DOM for them.
  function model(){
    var m = {};
    ['vanilla','chocolate','metab','bloat','bundle','bundle_van'].forEach(function(a){
      m[a] = { q: engine.displayQty(a) };
    });
    m.others = engine.otherLines(); // [{line, q}]
    return m;
  }

  function totals(m){
    var tb = m.vanilla.q + m.chocolate.q;
    var tier = tierIndex(tb);
    var ppb = (state.plan==='sub'?SUB:ONCE)[tier];
    var shakeSub = ppb*tb;
    var shakeBase = ONCE[0]*tb;
    var meals = tb*MEALS;
    var pillQty = m.metab.q + m.bloat.q;
    var hasShakes = tb>0;
    var ups = [];
    ['metab','bloat','bundle','bundle_van'].forEach(function(id){
      var q = m[id].q; if(!q) return;
      var unit = isBundleAlias(id) ? bundleUnit(state.plan, bundleQ(m) + bundleSiblingQty(m)) : upsellPrice(id, state.plan, hasShakes, pillQty);
      var sl = engine.serverLines(id);   // the recognized line carries the chosen flavor (variant)
      // Bundle flavors are known up front (bundleFlavors), so an optimistic line shows
      // its flavor chip from the first paint — before Shopify has returned the line.
      ups.push({ id:id, q:q, unit:unit, base:cfg.upsellBase[isBundleAlias(id) ? 'bundle' : id],
                 variant: sl.length ? sl[0].variant_title : (BUNDLE_FLAVOR[id] || null) });
    });
    var upSub = ups.reduce(function(s,u){return s+u.unit*u.q;},0);
    var upBase = ups.reduce(function(s,u){return s+u.base*u.q;},0);
    var grandSub = shakeSub + upSub;     // KNOWN aliases only — drives the shake/per-meal + switch-to-sub math
    var grandBase = shakeBase + upBase;
    // Foreign lines (e.g. an All-In-One Bundle in a flavor other than the single
    // configured V.bundle variant) are real cart value at their actual Shopify
    // prices. Fold them into the DISPLAYED cart total/subtotal/savings/free-ship so
    // nothing is silently dropped from the summary (they already render as lines).
    var othersFinal = m.others.reduce(function(s,o){return s + o.line.final_line_price/100;},0);
    var othersBase  = m.others.reduce(function(s,o){return s + o.line.original_line_price/100;},0);
    var cartNow  = grandSub + othersFinal;
    var cartBase = grandBase + othersBase;
    var bonus = bonusUnlocked(m) ? 2 : 0;
    var bonusVal = bonus*cfg.bonusValue;
    return { tb:tb, tier:tier, ppb:ppb, meals:meals, ppm: meals>0?grandSub/meals:0,
             ups:ups, pillQty:pillQty, hasShakes:hasShakes,
             grandSub:grandSub, grandBase:grandBase, savings:grandBase-grandSub,
             othersFinal:othersFinal, othersBase:othersBase,
             total: cartNow,                                  // ALL lines — the payable subtotal
             freeShip: cartNow>=FREE, bonus:bonus, bonusVal:bonusVal,
             displayBase: cartBase+bonusVal, displaySav:(cartBase-cartNow)+bonusVal,
             count: m.vanilla.q+m.chocolate.q+m.metab.q+m.bloat.q+bundleQ(m) + m.others.reduce(function(s,o){return s+o.q;},0) };
  }

  // ── GA4 dataLayer events (GTM → Stape) ─────────────────────────────────────
  // The drawer emits the behavioral events a bespoke UI can't get from
  // auto-instrumentation: add/remove with real display pricing + plan/cadence,
  // and begin_checkout. PURCHASES are deliberately absent — they're tracked
  // server-side via the Stape app's order webhooks (ad-blocker-proof and the
  // only way to see Appstle renewal orders). Gift/foreign lines are skipped.
  var DL_NAMES = {
    vanilla: 'Weight-Loss Superfood Meal (Vanilla-Caramel)',
    chocolate: 'Weight-Loss Superfood Meal (Chocolate)',
    metab: 'Metabolism Booster', bloat: 'Bloating Relief', bundle: 'All-In-One Bundle'
  };
  function dlUnit(alias, m, t){
    if(alias==='vanilla'||alias==='chocolate') return t.ppb;
    if(alias==='bundle') return bundleUnit(state.plan, m.bundle.q || 1);
    return upsellPrice(alias, state.plan, t.hasShakes, t.pillQty || 1);
  }
  function dlItem(alias, qty, unit){
    return { item_id: String(V[alias]||''), item_name: DL_NAMES[alias]||alias, price: +fmt(unit), quantity: qty };
  }
  function track(event, value, items){
    if(!window.dataLayer || !items.length) return;
    try {
      window.dataLayer.push({ ecommerce: null });
      var p = { event: event, plan: state.plan,
        ecommerce: { currency: window.__biomeCur || 'USD', value: +fmt(value), items: items } };
      if(state.plan==='sub'){ p.delivery_weeks = state.freq; }
      window.dataLayer.push(p);
    } catch(e){ /* tracking must never break the cart */ }
  }
  function trackQtyDelta(alias, delta){
    if(!alias || !DL_NAMES[alias] || !delta) return;
    var m = model(), t = totals(m);
    var unit = dlUnit(alias, m, t);
    track(delta > 0 ? 'add_to_cart' : 'remove_from_cart', unit * Math.abs(delta), [dlItem(alias, Math.abs(delta), unit)]);
  }

  var ICN = {
    minus:'<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8h10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>',
    plus:'<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>',
    rm:'<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 4h10M6 4V3a1 1 0 011-1h2a1 1 0 011 1v1M5 4l1 9a1 1 0 001 1h2a1 1 0 001-1l1-9" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    close:'<svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M4 4l12 12M16 4L4 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
    ship:'<svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M1 4.5h8v6.5H1zM9 6.5h2.7l2.3 2.3v2.2H9z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><circle cx="4" cy="12.5" r="1.4" fill="currentColor"/><circle cx="12" cy="12.5" r="1.4" fill="currentColor"/></svg>',
    lock:'<svg class="cd-lock-ic" width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="3" y="7" width="10" height="7" rx="1" stroke="currentColor" stroke-width="1.4"/><path d="M5 7V5a3 3 0 016 0v2" stroke="currentColor" stroke-width="1.4"/></svg>',
    check:'<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3.5 8.2l3 3 6-6.4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
  };
  function payRow(){
    return '<div class="pay-row">' + cfg.pays.map(function(p){
      return '<span class="pay-chip cd-pay-chip"><img src="'+p.url+'" alt="'+p.id+'" width="28" height="18"></span>';
    }).join('') + '</div>';
  }
  // ref = {alias:'vanilla'} for known SKUs, {key:'...'} for foreign lines.
  function stepper(ref, q){
    var idAttr = ref.alias ? 'data-alias="'+ref.alias+'"' : 'data-key="'+ref.key+'"';
    return '<span class="stepper">'+
      '<button class="stepper__btn" data-cart-dec '+idAttr+' aria-label="Decrease">'+ICN.minus+'</button>'+
      '<span class="stepper__val" data-cart-qty="'+(ref.alias||ref.key)+'">'+q+'</span>'+
      '<button class="stepper__btn stepper__btn--add" data-cart-inc '+idAttr+' aria-label="Increase">'+ICN.plus+'</button>'+
    '</span>';
  }
  // Flavor pill for a line's variant (e.g. the bundle's shake flavor), mirroring the
  // shake line's colored chip. Blank for single-variant lines ("Default Title"/none).
  function flavorChip(name){
    if(!name) return '';
    var low = String(name).toLowerCase();
    if(low.indexOf('default') >= 0) return '';
    // Gift-card denominations arrive as the variant title ("$10.00") — show them as a
    // whole dollar ("$10") to match the round-up convention used on the buy buttons.
    var label = String(name);
    var money = label.match(/^\$?\s*(\d+(?:\.\d+)?)\s*$/);
    if(money){ label = '$' + Math.ceil(parseFloat(money[1])); }
    var bg = 'var(--cream)', c = 'var(--ink-2)';
    if(low.indexOf('vanilla') >= 0){ bg = 'rgba(222,191,133,0.3)'; c = '#A88440'; }
    else if(low.indexOf('choc') >= 0){ bg = 'rgba(133,102,71,0.15)'; c = '#856647'; }
    return '<span class="cd-chip" style="background:'+bg+';color:'+c+'">'+label.toUpperCase()+'</span>';
  }
  // Price-block inner HTML (no wrapper) — shared by initial render + the in-place
  // repaint so a qty change updates just the text, never the image/SVG nodes.
  function shakePriceInner(q, t){
    var lineTotal=+fmt(t.ppb*q), baseTotal=+fmt(ONCE[0]*q), sav=+fmt(baseTotal-lineTotal);
    return (sav>0?'<span class="cl2__strike">$'+fmt(baseTotal)+'</span>':'')+
      '<span class="cl2__total">$'+fmt(lineTotal)+'</span>'+
      (sav>0?'<span class="cl2__sav-chip">Save $'+sav.toFixed(0)+'</span>':'');
  }
  function upsellPriceInner(u){
    var lineTotal=u.unit*u.q, baseTotal=u.base*u.q, sav=baseTotal-lineTotal;
    return (sav>0?'<span class="cl2__strike">$'+fmt(baseTotal)+'</span>':'')+
      '<span class="cl2__total">$'+fmt(lineTotal)+'</span>'+
      (sav>0?'<span class="cl2__sav-chip">Save $'+sav.toFixed(0)+'</span>':'');
  }
  function shakeLine(id, name, q, t){
    var chipBg = id==='vanilla' ? 'rgba(222,191,133,0.3)' : 'rgba(133,102,71,0.15)';
    var chipC = id==='vanilla' ? '#A88440' : '#856647';
    return '<div class="cl2"><div class="cl2__thumb cd-thumb">'+
      '<img src="'+cfg.img.shake+'" alt="'+name+'" width="64" height="64" loading="lazy" class="cd-thumb-img"></div>'+
      '<div class="cl2__body"><div class="cl2__title-row"><span class="cd-line-title">Weight-Loss Superfood Meal</span>'+
      '<button class="cl2__rm" data-cart-remove data-alias="'+id+'" aria-label="Remove">'+ICN.rm+'</button></div>'+
      '<div class="cl2__meta"><span class="cd-chip" style="background:'+chipBg+';color:'+chipC+'">'+name.toUpperCase()+'</span>'+
      '<span class="cl2__dot">·</span><span>'+MEALS+' meals/box</span></div>'+
      '<div class="cl2__bot">'+stepper({alias:id}, q)+'<div class="cl2__price" data-cart-lineprice="'+id+'">'+
      shakePriceInner(q, t)+
      '</div></div></div></div>';
  }
  var UPS_NAME = { metab:'Metabolism Booster', bloat:'Bloating Relief', bundle:'All-In-One Bundle', bundle_van:'All-In-One Bundle' };
  var UPS_SUB = { metab:'Pairs with your shake. Reduces afternoon cravings.', bloat:'Gentle digestive enzymes for the first two weeks.', bundle:'Shake + Metabolism + Bloating. Best for 90 days.' };
  function upsellLine(u, t){
    var cartExcl = t.hasShakes && (u.id==='metab'||u.id==='bloat');
    var pctOff = u.unit<u.base ? Math.round((1-u.unit/u.base)*100) : 0;
    var chip = cartExcl
      ? '<span class="cd-chip cd-chip--excl">CART EXCLUSIVE <span class="cd-chip__pct">−'+pctOff+'%</span></span>'
      : (pctOff>0 ? '<span class="cd-chip cd-chip--save">'+(state.plan==='sub'?'SUBSCRIPTION':'SAVINGS')+' <span class="cd-chip__pct">−'+pctOff+'%</span></span>'
      : '<span class="cd-chip cd-chip--single">SINGLE BOTTLE</span>');
    return '<div class="cl2 cl2--upsell"><div class="cl2__thumb cd-thumb">'+
      '<img src="'+cfg.img[u.id]+'" alt="'+UPS_NAME[u.id]+'" width="64" height="64" loading="lazy" class="cd-thumb-img"></div>'+
      '<div class="cl2__body"><div class="cl2__title-row"><span class="cd-line-title">'+UPS_NAME[u.id]+'</span>'+
      '<button class="cl2__rm" data-cart-remove data-alias="'+u.id+'" aria-label="Remove">'+ICN.rm+'</button></div>'+
      '<div class="cl2__meta">'+flavorChip(u.variant)+chip+'</div>'+
      '<div class="cl2__bot">'+stepper({alias:u.id}, u.q)+'<div class="cl2__price" data-cart-lineprice="'+u.id+'">'+
      upsellPriceInner(u)+
      '</div></div></div></div>';
  }
  function genericLine(o){
    var it = o.line;
    var img = it.image ? it.image + (it.image.indexOf('?') >= 0 ? '&' : '?') + 'width=240' : '';
    // Genuinely-free items say "Free"; a line discounted below its original
    // price keeps the strike-through (compare-at prices aren't in cart.js).
    var strike = it.original_line_price > it.final_line_price
      ? '<span class="cl2__strike">$'+fmt(it.original_line_price/100)+'</span>' : '';
    var total = it.final_line_price === 0 ? 'Free' : '$'+fmt(it.final_line_price/100);
    var flav = flavorChip(it.variant_title);
    return '<div class="cl2"><div class="cl2__thumb cd-thumb">'+
      (img?'<img src="'+img+'" alt="'+it.product_title+'" width="64" height="64" loading="lazy" class="cd-thumb-img">':'')+'</div>'+
      '<div class="cl2__body"><div class="cl2__title-row"><span class="cd-line-title cd-line-title--plain">'+it.product_title+'</span>'+
      '<button class="cl2__rm" data-cart-remove data-key="'+it.key+'" aria-label="Remove">'+ICN.rm+'</button></div>'+
      (flav?'<div class="cl2__meta">'+flav+'</div>':'')+
      '<div class="cl2__bot">'+stepper({key:it.key}, o.q)+'<div class="cl2__price">'+strike+'<span class="cl2__total">'+total+'</span></div></div></div></div>';
  }
  function milestone(t){
    if(t.tb===0){ return shipOnly(t.total); } // no shakes in cart → single free-shipping bar (prototype MilestoneBar→ShipOnlyBar)
    var pct = t.tb===0?0:(t.tb===1?37.5:(t.tb===2?87.5:100));
    var fill = t.tier===2?'var(--terracotta)':'var(--forest)';
    var sav1=(ONCE[0]-ONCE[1]).toFixed(0), sav2=(ONCE[0]-ONCE[2]).toFixed(0);
    function dot(r,a){ var c=r?(a||(t.tier===2?'var(--terracotta)':'var(--forest)')):'var(--cream-2)'; var b=r?(a||(t.tier===2?'var(--terracotta)':'var(--forest)')):'var(--sage)'; return 'background:'+c+';border-color:'+b+';'; }
    var ship = t.freeShip?'Free shipping unlocked':'Free shipping at $'+FREE;
    return '<div class="ms-twin '+(t.tier===2?'ms-twin--all-full':'')+'"><div class="ms-twin__bar ms-twin__bar--tall" style="--ms-pct:'+pct+'%">'+
      '<div class="ms-twin__fill '+(pct>=100?'ms-twin__fill--full':'')+'" style="width:'+pct+'%;background:'+fill+'"></div>'+
      '<span class="ms-twin__inside '+(t.freeShip?'ms-twin__inside--on':'')+'"><span class="ms-twin__inside-icon">'+ICN.ship+'</span>'+ship+'</span>'+
      '<span class="ms-twin__dot" style="left:0%;'+dot(t.tb>=1)+'"></span><span class="ms-twin__dot" style="left:100%;'+dot(t.tier===2,'var(--terracotta)')+'"></span></div>'+
      '<div class="ms-twin__row ms-twin__row--bot">'+
      '<span class="ms-twin__marker ms-twin__marker--l cd-mk0"><span class="ms-twin__marker-qty" style="color:'+(t.tb>=1?'var(--ink)':'var(--ink-2)')+'">1 Unit</span></span>'+
      '<span class="ms-twin__marker cd-mk50"><span class="ms-twin__marker-qty" style="color:'+(t.tb>=2?'var(--ink)':'var(--ink-2)')+'">2</span><span class="ms-twin__marker-perk" style="color:'+(t.tb>=2?'var(--forest)':'var(--sage)')+'">−$'+sav1+'<span class="ms-twin__marker-perk-unit"> / unit</span></span></span>'+
      '<span class="ms-twin__marker ms-twin__marker--r cd-mk100"><span class="ms-twin__marker-qty" style="color:'+(t.tier===2?'var(--ink)':'var(--ink-2)')+'">3+</span><span class="ms-twin__marker-perk" style="color:'+(t.tier===2?'var(--terracotta-2)':'var(--sage)')+'">−$'+sav2+'<span class="ms-twin__marker-perk-unit"> / unit</span></span></span>'+
      '</div></div>';
  }
  function shipOnly(sub){
    var pct = Math.min(100,(sub/FREE)*100); var on = sub>=FREE;
    return '<div class="ms-twin ms-twin--ship-only"><div class="ms-twin__bar ms-twin__bar--tall" style="--ms-pct:'+pct+'%">'+
      '<div class="ms-twin__fill '+(pct>=100?'ms-twin__fill--full':'')+'" style="width:'+pct+'%;background:var(--forest)"></div>'+
      '<span class="ms-twin__inside '+(on?'ms-twin__inside--on':'')+'"><span class="ms-twin__inside-icon">'+ICN.ship+'</span>'+(on?'Free shipping unlocked':'Add $'+Math.ceil(FREE-sub)+' for free shipping')+'</span>'+
      '<span class="ms-twin__dot" style="left:100%;'+(on?'background:var(--forest);border-color:var(--forest);':'background:var(--cream-2);border-color:var(--cream-2);')+'"></span></div></div>';
  }
  // Update the shipping bar IN PLACE so the fill slides (CSS width transition) rather than
  // being recreated by innerHTML (a brand-new node can't animate). Full-replace only when the
  // bar TYPE changes (shakes ↔ no shakes) — matches the prototype, where the fill node persists.
  function paintBar(sb, t){
    var wantShip = t.tb===0;
    if((!!sb.querySelector('.ms-twin--ship-only')) !== wantShip){ sb.innerHTML = wantShip ? shipOnly(t.total) : milestone(t); return; }
    var pct = wantShip ? Math.min(100,(t.total/FREE)*100) : (t.tb===1?37.5:(t.tb===2?87.5:100));
    var on  = wantShip ? (t.total>=FREE) : t.freeShip;
    var fillEl = sb.querySelector('.ms-twin__fill');
    if(fillEl){
      fillEl.style.width = pct+'%';
      fillEl.style.background = wantShip ? 'var(--forest)' : (t.tier===2?'var(--terracotta)':'var(--forest)');
      fillEl.classList.toggle('ms-twin__fill--full', pct>=100);
    }
    var barEl = sb.querySelector('.ms-twin__bar'); if(barEl){ barEl.style.setProperty('--ms-pct', pct+'%'); }
    var insideEl = sb.querySelector('.ms-twin__inside');
    if(insideEl){
      insideEl.classList.toggle('ms-twin__inside--on', on);
      var ic = insideEl.querySelector('.ms-twin__inside-icon');
      var txt = wantShip ? (on?'Free shipping unlocked':'Add $'+Math.ceil(FREE-t.total)+' for free shipping') : (t.freeShip?'Free shipping unlocked':'Free shipping at $'+FREE);
      if(ic && ic.nextSibling && ic.nextSibling.nodeType===3){ ic.nextSibling.textContent = txt; }
    }
    if(wantShip){
      var d1 = sb.querySelector('.ms-twin__dot'); if(d1){ d1.style.cssText = 'left:100%;'+(on?'background:var(--forest);border-color:var(--forest);':'background:var(--cream-2);border-color:var(--cream-2);'); }
      return;
    }
    var wrap = sb.querySelector('.ms-twin'); if(wrap){ wrap.classList.toggle('ms-twin--all-full', t.tier===2); }
    var accent = t.tier===2?'var(--terracotta)':'var(--forest)';
    var dots = barEl ? barEl.querySelectorAll('.ms-twin__dot') : [];
    if(dots[0]){ dots[0].style.cssText = 'left:0%;'+(t.tb>=1?'background:'+accent+';border-color:'+accent+';':'background:var(--cream-2);border-color:var(--sage);'); }
    if(dots[1]){ dots[1].style.cssText = 'left:100%;'+(t.tier===2?'background:var(--terracotta);border-color:var(--terracotta);':'background:var(--cream-2);border-color:var(--sage);'); }
    var row = sb.querySelector('.ms-twin__row--bot');
    if(row){
      var sav1=(ONCE[0]-ONCE[1]).toFixed(0), sav2=(ONCE[0]-ONCE[2]).toFixed(0);
      row.innerHTML =
        '<span class="ms-twin__marker ms-twin__marker--l cd-mk0"><span class="ms-twin__marker-qty" style="color:'+(t.tb>=1?'var(--ink)':'var(--ink-2)')+'">1 Unit</span></span>'+
        '<span class="ms-twin__marker cd-mk50"><span class="ms-twin__marker-qty" style="color:'+(t.tb>=2?'var(--ink)':'var(--ink-2)')+'">2</span><span class="ms-twin__marker-perk" style="color:'+(t.tb>=2?'var(--forest)':'var(--sage)')+'">−$'+sav1+'<span class="ms-twin__marker-perk-unit"> / unit</span></span></span>'+
        '<span class="ms-twin__marker ms-twin__marker--r cd-mk100"><span class="ms-twin__marker-qty" style="color:'+(t.tier===2?'var(--ink)':'var(--ink-2)')+'">3+</span><span class="ms-twin__marker-perk" style="color:'+(t.tier===2?'var(--terracotta-2)':'var(--sage)')+'">−$'+sav2+'<span class="ms-twin__marker-perk-unit"> / unit</span></span></span>';
    }
  }
  // Empty-state quick-add uses curated stills (not the products' featured
  // images): the AIO "trying out" render and the vanilla single pack.
  // NOTE the two different widths — they are NOT interchangeable. `shake` fills a
  // 64px .qa-row__thumb, so 160 is the right 2x ask. `bundle` fills .empty-feat__img:
  // a 240px SQUARE (216 on mobile) with .cd-cover-img = object-fit:cover — and the
  // render is 4:3 (640x480 at width=640), so cover scales it by HEIGHT: 240 CSS px tall
  // = 480 device px at 2x = needs 480px of source height = width=640. The perf pass
  // (500c4c3) right-sized BOTH to 160 (160x120 → drawn ~4x upscaled on retina = the
  // blur); the old width="64" height="64" on the tag is what made it look like a
  // thumbnail. Keep the attributes honest and size by the cover-binding dimension.
  var QA_IMG = {
    bundle: 'https://cdn.shopify.com/s/files/1/0735/8742/5546/files/trying-out.avif?v=1780539920&width=640',
    shake: 'https://cdn.shopify.com/s/files/1/0735/8742/5546/files/single-pack-vanilla-caramel.png?v=1780976198&width=160'
  };
  // Quick-add rows (bundle feature + flavor picker + pill bottles). Shared by the
  // drawer empty state and the cart-page empty state — each wraps these its own way.
  function qaRows(m){
    var rows = '';
    if(!bundleQ(m)) rows += '<button type="button" class="empty-feat" data-cart-add="bundle"><span class="empty-feat__img"><img src="'+QA_IMG.bundle+'" alt="All-in-One Bundle" width="240" height="240" loading="lazy" class="cd-cover-img"></span><span class="empty-feat__name">All-In-One Bundle</span><span class="empty-feat__prices"><span class="empty-feat__now">$'+fmt(bundleUnit('sub',1))+'</span><span class="empty-feat__strike">$'+fmt(cfg.upsellBase.bundle)+'</span></span><span class="empty-feat__cta">'+ICN.plus+' Add to cart</span></button>';
    rows += '<div class="qa-row qa-row--flavor"><div class="qa-row__thumb cd-bg-cream"><img src="'+QA_IMG.shake+'" alt="Weight-Loss Meal" width="64" height="64" loading="lazy" class="cd-cover-img"></div>'+
      '<div class="qa-row__copy"><div class="qa-row__name">Weight-Loss Meal</div><div class="qa-row__prices"><span class="qa-row__now">$'+fmt(SUB[0])+'</span><span class="qa-row__strike">$'+fmt(ONCE[0])+'</span></div></div>'+
      '<div class="qa-row__flavors"><button class="qa-row__flavor" data-cart-add="vanilla">Vanilla</button><button class="qa-row__flavor" data-cart-add="chocolate">Chocolate</button></div></div>';
    [['metab','Metabolism Booster+'],['bloat','Bloating Relief+']].forEach(function(p){
      rows += '<button type="button" class="qa-row" data-cart-add="'+p[0]+'"><span class="qa-row__thumb cd-bg-cream"><img src="'+cfg.img[p[0]]+'" alt="'+p[1]+'" width="64" height="64" loading="lazy" class="cd-cover-img"></span><span class="qa-row__copy"><span class="qa-row__name">'+p[1]+'</span><span class="qa-row__prices"><span class="qa-row__now">$56</span><span class="qa-row__strike">$66</span></span></span><span class="qa-row__add">'+ICN.plus+' Add</span></button>';
    });
    return rows;
  }
  function quickAdd(m){
    return '<div class="empty"><div class="empty__qa"><div class="empty__qa-title">Add our best sellers</div>'+qaRows(m)+'</div></div>';
  }

  // The cadence the supply math wants (each box/bottle ≈ 2 weeks), mirroring
  // the PDP. Pure-shake carts snap on shake boxes; pure-pill carts on the
  // larger pill qty; mixed/empty carts return null (left alone).
  function snapTargetFreq(m){
    if(state.plan!=='sub') return null;
    var shakeBoxes = m.vanilla.q + m.chocolate.q;
    var metabQ = m.metab.q, bloatQ = m.bloat.q;
    var onlyShakes = shakeBoxes>0 && metabQ===0 && bloatQ===0;
    var onlyPills  = shakeBoxes===0 && (metabQ>0 || bloatQ>0);
    if(!onlyShakes && !onlyPills) return null;
    var driving = onlyShakes ? shakeBoxes : Math.max(metabQ, bloatQ);
    var target = Math.min(driving*2, 12);
    return [2,4,6,8,10,12].reduce(function(a,b){ return Math.abs(b-target)<Math.abs(a-target)?b:a; });
  }
  // A manual freq pick persists until the cart QUANTITY changes (the qty
  // handlers clear freqManual, re-enabling the snap).
  function autoSnapFreq(m){
    if(state.freqManual) return;
    var closest = snapTargetFreq(m);
    if(closest && closest!==state.freq){ state.freq=closest; engine.setAttr('delivery_weeks', String(state.freq)); }
  }

  // ── In-place repaint ───────────────────────────────────────────────────────
  // A qty change within the same cart STRUCTURE updates only the changed values
  // (qtys, line prices, totals, milestone) in place — leaving every <img>/<svg>
  // untouched — so the drawer never flashes. Structural changes (a line added or
  // removed, bonus row toggling, the upsell set changing, or any non-biome product
  // present) fall back to a full render.
  var lastSig = '', lastMs = '';
  function msSig(t){ if(t.tb===0){ return 'ship|'+Math.round(Math.min(100,(t.total/FREE)*100))+'|'+t.freeShip; } return (t.tb===1?37.5:(t.tb===2?87.5:100))+'|'+t.freeShip+'|'+t.tier; }
  function structSig(m, t){
    return [
      t.count === 0, m.vanilla.q > 0, m.chocolate.q > 0,
      t.ups.map(function(u){ return u.id; }).join(','),
      t.bonus > 0,
      upsellSlotsFree(m).join(','),
      m.others.length
    ].join('|');
  }
  function totalRowInner(t){
    return '<span>Total'+(t.displaySav>0?'<span class="cd-ft-save">You save $'+fmt(t.displaySav)+'</span>':'')+'</span>'+
      '<span class="cd-ft-amount">'+(t.displaySav>0?'<span class="cd-ft-strike">$'+fmt(t.displayBase)+'</span>':'')+'<span>$'+fmt(t.total)+'</span></span>';
  }
  function repaint(){
    var m = model(), t = totals(m);
    if(t.count===0 || m.others.length>0 || structSig(m,t)!==lastSig){ render(); return; }
    function setLine(alias, q, priceInner){
      var qEl = content.querySelector('[data-cart-qty="'+alias+'"]'); if(qEl){ qEl.textContent = q; }
      var pEl = content.querySelector('[data-cart-lineprice="'+alias+'"]'); if(pEl){ pEl.innerHTML = priceInner; }
    }
    if(m.vanilla.q){ setLine('vanilla', m.vanilla.q, shakePriceInner(m.vanilla.q, t)); }
    if(m.chocolate.q){ setLine('chocolate', m.chocolate.q, shakePriceInner(m.chocolate.q, t)); }
    t.ups.forEach(function(u){ setLine(u.id, u.q, upsellPriceInner(u)); });
    var hd = content.querySelector('.cd-hd-meta'); if(hd && t.meals>0){ hd.textContent = t.meals+' meals · $'+fmt(t.ppm)+'/meal'; }
    var pd = content.querySelector('.plan-chip__discount'); if(pd){ pd.innerHTML = '−$'+(t.tb>=3?20:10)+'<span class="plan-chip__unit">/box</span>'; }
    var tr = content.querySelector('.ft-row--total'); if(tr){ tr.innerHTML = totalRowInner(t); }
    var ssav = content.querySelector('.ft-microline__sav'); if(ssav){ ssav.textContent = switchSavText(switchSavOf(t)); }
    var ms = msSig(t); if(ms !== lastMs){ var sb = content.querySelector('.ft-shipbar'); if(sb){ paintBar(sb, t); } lastMs = ms; }
    var fs = content.querySelector('[data-cart-freq]'); if(fs){ fs.value = String(state.freq); }
    var notAdded = upsellSlotsFree(m);
    notAdded.forEach(function(id){     // keep every upsell card's subscribe price live
      var price = upsellPrice(id, 'sub', t.hasShakes, t.pillQty + ((id==='metab'||id==='bloat')?1:0));
      var pct = Math.max(0, Math.round((1-price/cfg.upsellBase[id])*100));
      var pr = content.querySelector('[data-cart-upsell-price="'+id+'"]');
      if(pr){ pr.innerHTML = (pct>0?'<span class="cd-pct-badge">−'+pct+'%</span><span class="cd-upsell-strike">$'+fmt(cfg.upsellBase[id])+'</span>':'')+'<span class="cd-upsell-price">$'+price+'</span>'; }
    });
    updateBadge(t.count);
  }

  function render(){
    var prevScroll = content.querySelector('.drawer__scroll');
    var savedTop = prevScroll ? prevScroll.scrollTop : 0;
    var m = model();
    var t = totals(m);
    var isEmpty = t.count===0;
    var head = '<div class="drawer__hd"><div class="cd-hd-titles"><span class="drawer__title">Your cart</span>'+
      (isEmpty?'<span class="cd-hd-meta cd-hd-meta--empty">— empty</span>':(t.meals>0?'<span class="cd-hd-meta">'+t.meals+' meals · $'+fmt(t.ppm)+'/meal</span>':''))+
      '</div><button data-cart-close class="cd-close" aria-label="Close">'+ICN.close+'</button></div>';

    var body;
    if(isEmpty){
      body = '<div class="drawer__body">'+quickAdd(m)+'</div>';
    } else {
      var planRow = '<div class="drawer__sticky-top">'+buildPlanRow(t)+'</div>';

      var lines = buildLines(m, t);

      var switchLine = switchLineHTML(t);
      var totalRow = '<div class="ft-row ft-row--total">'+totalRowInner(t)+'</div>';
      var foot = '<div class="drawer__ft drawer__ft--v2"><div class="ft-totals">'+switchLine+totalRow+'</div>'+
        '<div class="drawer__pays-wrap cd-pays-wrap">'+payRow()+'</div>'+
        '<button class="btn btn--forest btn--block btn--lg cd-checkout-btn" data-cart-checkout>'+ICN.lock+'Secure checkout</button>'+
        '<div class="ft-shipbar">'+milestone(t)+'</div></div>';

      body = '<div class="drawer__body">'+planRow+'<div class="drawer__scroll"><div class="cl2-list">'+lines+'</div></div></div>'+foot;
    }
    var footEmpty = isEmpty ? '<div class="drawer__ft drawer__ft--v2"><div class="drawer__pays-wrap">'+payRow()+'</div><button class="btn btn--disabled btn--block btn--lg cd-checkout-btn" disabled>'+ICN.lock+'Add a flavor to checkout</button></div>' : '';
    content.innerHTML = head + body + footEmpty;
    var nextScroll = content.querySelector('.drawer__scroll');
    if(nextScroll && savedTop){ nextScroll.scrollTop = savedTop; }
    updateBadge(t.count);
    lastSig = structSig(m, t);
    lastMs = msSig(t);
  }

  // ── Shared builders (drawer + cart page) ────────────────────────────────────
  // Plan toggle + delivery-cadence row. Markup identical to the original drawer
  // plan row; reused by the drawer sticky-top and the page summary card.
  function buildPlanRow(t){
    return '<div class="plan-row"><div class="plan-chip" data-sel="'+state.plan+'">'+
      '<button class="plan-chip__opt '+(state.plan==='sub'?'plan-chip__opt--on':'')+'" data-cart-plan="sub">Subscribe <span class="mono-sm plan-chip__discount">−$'+(t.tb>=3?20:10)+'<span class="plan-chip__unit">/box</span></span></button>'+
      '<button class="plan-chip__opt '+(state.plan==='once'?'plan-chip__opt--on':'')+'" data-cart-plan="once">One-time</button></div>'+
      '<div class="freq-row freq-row--inline '+(state.plan==='sub'?'':'freq-row--hidden')+'"><span class="freq-row__lbl">Every</span>'+
      '<select class="freq-row__sel" data-cart-freq>'+[2,4,6,8,10,12].map(function(w){return '<option value="'+w+'" '+(w===state.freq?'selected':'')+'>'+w+' weeks</option>';}).join('')+'</select></div></div>';
  }

  // Line-item list: shake + upsell + foreign lines + free-bonus row + the
  // cart-exclusive upsell cards. Shared by the drawer body and the page's left column.
  function buildLines(m, t){
    var lines = '';
    if(m.vanilla.q) lines += shakeLine('vanilla','Vanilla-Caramel',m.vanilla.q,t);
    if(m.chocolate.q) lines += shakeLine('chocolate','Chocolate',m.chocolate.q,t);
    t.ups.forEach(function(u){ lines += upsellLine(u,t); });
    m.others.forEach(function(o){ lines += genericLine(o); });
    if(t.bonus>0){
      lines += '<div class="bonus"><div class="bonus__icon"><svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="2" y="6" width="14" height="9" rx="1" stroke="currentColor" stroke-width="1.3"/><path d="M2 9h14M9 6v9" stroke="currentColor" stroke-width="1.3"/></svg></div><div class="cd-bonus-main"><div class="cd-bonus-lead">'+t.bonus+' <span class="cd-bonus-em">free bonuses</span> included</div><div class="mono-sm cd-bonus-sub">THE BIOME METHOD · 15-DAY RESET</div></div><span class="mono-sm cd-bonus-val">$'+t.bonusVal+' VALUE</span></div>';
    }
    // Upsell list — every not-yet-added upgrade as its own card (original style),
    // stacked vertically (no carousel / dots). Each whole card adds it on tap.
    var notAdded = upsellSlotsFree(m);
    if(notAdded.length){
      var upCards = notAdded.map(function(id){
        var price = upsellPrice(id, 'sub', t.hasShakes, t.pillQty + ((id==='metab'||id==='bloat')?1:0)); // always the subscribe price on the upsell card
        var pct = Math.max(0, Math.round((1-price/cfg.upsellBase[id])*100));
        return '<div class="upsell__body cd-upsell-body" data-cart-add="'+id+'" role="button" tabindex="0">'+
          '<span class="upsell__check">'+ICN.check+'</span>'+
          '<div class="upsell__thumb cd-thumb"><img src="'+cfg.img[id]+'" alt="'+UPS_NAME[id]+'" width="64" height="64" loading="lazy" class="cd-cover-img"></div>'+
          '<div class="upsell__content cd-upsell-content"><div class="cd-line-title">'+UPS_NAME[id]+'</div>'+
          '<div class="body-sm cd-upsell-sub">'+UPS_SUB[id]+'</div>'+
          '<div class="cd-upsell-pricing" data-cart-upsell-price="'+id+'">'+(pct>0?'<span class="cd-pct-badge">−'+pct+'%</span><span class="cd-upsell-strike">$'+fmt(cfg.upsellBase[id])+'</span>':'')+'<span class="cd-upsell-price">$'+price+'</span></div></div></div>';
      }).join('');
      lines += '<div class="upsell"><div class="upsell__head"><span class="mono-sm cd-upsell-head">Upgrade my order!</span></div>'+
        upCards+'</div>';
    }
    return lines;
  }

  // ── Cart page surface (sections/biome-cart.liquid) ───────────────────────────
  // A full-page /cart rendered from the SAME engine + pricing + line builders as the
  // drawer, laid out as the design's two columns: items left, sticky summary right.
  // No-op everywhere the page mount is absent.

  // Page structural signature = drawer structSig + the bits that change the summary
  // STRUCTURE (savings row present?, free-ship copy, sub↔once microline shape).
  function structSigPage(m, t){ return structSig(m, t) + '|' + (t.displaySav>0?1:0) + '|' + (t.freeShip?1:0) + '|' + state.plan; }

  function pageSummary(m, t){
    var subStrike = t.displaySav>0 ? '<span class="cartpg-sum__strike">$'+fmt(t.displayBase)+'</span>' : '';
    var saveRow = t.displaySav>0 ? '<div class="cartpg-sum__row cartpg-sum__row--save"><span>You save</span><span data-cart-page-save>−$'+fmt(t.displaySav)+'</span></div>' : '';
    var totalStrike = t.displaySav>0 ? '<span class="cartpg-sum__strike cartpg-sum__strike--lg">$'+fmt(t.displayBase)+'</span>' : '';
    var ship = t.freeShip ? 'Free' : 'Calculated at checkout';
    var fine = t.freeShip ? 'Taxes and discount codes are calculated at checkout.' : 'Shipping, taxes and discount codes are calculated at checkout.';
    return '<div class="cartpg-sum"><div class="cartpg-sum__hd">Order summary</div>'+
      buildPlanRow(t)+
      '<div class="cartpg-sum__rows">'+
        '<div class="cartpg-sum__row"><span>Subtotal</span><span class="cartpg-sum__sub" data-cart-page-subtotal>'+subStrike+'$'+fmt(t.total)+'</span></div>'+
        saveRow+
        '<div class="cartpg-sum__row cartpg-sum__row--ship"><span>Shipping</span><span>'+ship+'</span></div>'+
      '</div>'+
      switchLineHTML(t)+
      '<div class="cartpg-sum__total"><span>Total</span><span class="cartpg-sum__total-val" data-cart-page-total>'+totalStrike+'$'+fmt(t.total)+' <span class="cartpg-sum__cur">USD</span></span></div>'+
      '<button class="btn btn--forest btn--block btn--lg cartpg-sum__checkout" data-cart-checkout>'+ICN.lock+'Secure checkout</button>'+
      '<div class="cartpg-sum__express" data-cart-express-slot></div>'+
      '<div class="ft-shipbar" style="margin-top:16px">'+milestone(t)+'</div>'+
      '<div class="cartpg-sum__pays">'+payRow()+'</div>'+
      '<div class="cartpg-sum__fine">'+fine+'</div></div>';
  }

  function pageStickyBar(t){
    var strike = t.displaySav>0 ? '<span class="cartpg-stickybar__strike">$'+fmt(t.displayBase)+'</span>' : '';
    return '<div class="cartpg-stickybar" role="region" aria-label="Checkout">'+
      '<div class="cartpg-stickybar__info"><span class="cartpg-stickybar__lbl">Total</span>'+
      '<span class="cartpg-stickybar__val" data-cart-page-stickyval>'+strike+'$'+fmt(t.total)+'</span></div>'+
      '<button class="btn btn--forest btn--lg cartpg-stickybar__btn" data-cart-checkout>'+ICN.lock+'Secure checkout</button></div>';
  }

  function pageEmpty(m){
    return '<div class="cartpg__empty"><div class="cartpg-empty"><div class="cartpg-empty__icon" aria-hidden="true">'+
      '<svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 7h14l-1.2 9.5a1 1 0 01-1 .9H7.2a1 1 0 01-1-.9z"/><path d="M5 7L4 4H2.5"/><circle cx="9" cy="20.5" r="0.8" fill="currentColor"/><circle cx="16" cy="20.5" r="0.8" fill="currentColor"/><path d="M9.5 7V5.5a2.5 2.5 0 015 0V7"/></svg>'+
      '</div><p class="cartpg-empty__msg">Your cart is empty</p></div>'+
      '<div class="cartpg-bestsellers"><div class="empty__qa"><div class="empty__qa-title">Add our best sellers</div>'+qaRows(m)+'</div></div></div>';
  }

  // Full page render — empty state or the two-column grid. First paint + structural changes.
  // The Shopify express buttons can't be created in JS, so they live in a parked Liquid
  // container; park/mount relocate the node out of and into the summary slot around each full
  // re-render so page.innerHTML never destroys them. (repaintPage doesn't touch innerHTML, so
  // qty ticks leave them in place — no re-init flicker.)
  function parkExpress(){ if(!expressHome) return; var n = page.querySelector('[data-cart-express]'); if(n){ expressHome.appendChild(n); } }
  function mountExpress(){ if(!expressHome) return; var n = expressHome.querySelector('[data-cart-express]'); var slot = page.querySelector('[data-cart-express-slot]'); if(n && slot){ slot.appendChild(n); } }

  function renderPage(){
    if(!page) return;
    var m = model(), t = totals(m);
    var isEmpty = t.count===0;
    parkExpress();   // save the express node from the innerHTML wipe below
    if(pageCount){ pageCount.hidden = isEmpty; pageCount.textContent = isEmpty ? '' : ' ('+t.count+')'; }
    if(pageHead){ pageHead.style.display = isEmpty ? 'none' : ''; }   // hide the page header entirely in the empty state
    if(isEmpty){
      page.innerHTML = pageEmpty(m);
    } else {
      page.innerHTML =
        '<div class="cartpg__grid"><div class="cartpg__items"><div class="cl2-list">'+buildLines(m, t)+'</div></div>'+
        '<aside class="cartpg__summary">'+pageSummary(m, t)+'</aside></div>'+
        pageStickyBar(t);
    }
    mountExpress();  // relocate into the summary slot (no-op on the empty state — no slot)
    lastSigPage = structSigPage(m, t);
    lastMsPage = msSig(t);
    updateBadge(t.count);
  }

  // Engine/interaction entry point: full render on a structural change, else update
  // the shared line nodes + summary/sticky totals + milestone in place (no flash).
  function repaintPage(){
    if(!page) return;
    var m = model(), t = totals(m);
    if(t.count===0 || m.others.length>0 || structSigPage(m, t)!==lastSigPage){ renderPage(); return; }
    function setLine(alias, q, priceInner){
      var qEl = page.querySelector('[data-cart-qty="'+alias+'"]'); if(qEl){ qEl.textContent = q; }
      var pEl = page.querySelector('[data-cart-lineprice="'+alias+'"]'); if(pEl){ pEl.innerHTML = priceInner; }
    }
    if(m.vanilla.q){ setLine('vanilla', m.vanilla.q, shakePriceInner(m.vanilla.q, t)); }
    if(m.chocolate.q){ setLine('chocolate', m.chocolate.q, shakePriceInner(m.chocolate.q, t)); }
    t.ups.forEach(function(u){ setLine(u.id, u.q, upsellPriceInner(u)); });
    var pd = page.querySelector('.plan-chip__discount'); if(pd){ pd.innerHTML = '−$'+(t.tb>=3?20:10)+'<span class="plan-chip__unit">/box</span>'; }
    var subEl = page.querySelector('[data-cart-page-subtotal]'); if(subEl){ subEl.innerHTML = (t.displaySav>0?'<span class="cartpg-sum__strike">$'+fmt(t.displayBase)+'</span>':'')+'$'+fmt(t.total); }
    var saveEl = page.querySelector('[data-cart-page-save]'); if(saveEl){ saveEl.textContent = '−$'+fmt(t.displaySav); }
    var totEl = page.querySelector('[data-cart-page-total]'); if(totEl){ totEl.innerHTML = (t.displaySav>0?'<span class="cartpg-sum__strike cartpg-sum__strike--lg">$'+fmt(t.displayBase)+'</span>':'')+'$'+fmt(t.total)+' <span class="cartpg-sum__cur">USD</span>'; }
    var stickyEl = page.querySelector('[data-cart-page-stickyval]'); if(stickyEl){ stickyEl.innerHTML = (t.displaySav>0?'<span class="cartpg-stickybar__strike">$'+fmt(t.displayBase)+'</span>':'')+'$'+fmt(t.total); }
    var ms = msSig(t); if(ms !== lastMsPage){ var sb = page.querySelector('.ft-shipbar'); if(sb){ paintBar(sb, t); } lastMsPage = ms; }
    if(pageCount){ pageCount.hidden = false; pageCount.textContent = ' ('+t.count+')'; }
    updateBadge(t.count);
  }

  function updateBadge(n){
    document.querySelectorAll('[data-biome-cart-count]').forEach(function(b){ b.textContent=n; b.hidden = n===0; });
    // Compact-nav cart icon only shows once there's something in the cart.
    document.querySelectorAll('[data-biome-cart-compact]').forEach(function(b){ b.setAttribute('aria-hidden', n===0 ? 'true' : 'false'); });
  }

  function switchLineHTML(t){
    return state.plan==='sub'
      ? '<div class="ft-microline"><span>Skip or cancel anytime</span></div>'
      : '<button type="button" data-cart-plan="sub" class="ft-microline ft-microline--toggle"><span class="ft-microline__check"></span><span>Switch to subscription<span class="ft-microline__sav">'+switchSavText(switchSavOf(t))+'</span></span></button>';
  }
  function setPlan(p){
    state.plan=p; engine.setAttr('plan', p); autoSnapFreq(model());
    // Switch the plan toggle IN PLACE so the .plan-chip::before pill SLIDES between Subscribe and
    // One-time — a full render() recreates the node at the target position, so the pill would jump.
    // Prices / totals / milestone reconcile via repaint(); only the sub↔once footer microline needs
    // swapping. Fall back to a full render if the toggle isn't mounted yet.
    var chip = content.querySelector('.plan-chip');
    if(!chip){ render(); return; }
    chip.setAttribute('data-sel', p);
    chip.querySelectorAll('.plan-chip__opt').forEach(function(b){ b.classList.toggle('plan-chip__opt--on', b.getAttribute('data-cart-plan')===p); });
    var fr = content.querySelector('.freq-row'); if(fr){ fr.classList.toggle('freq-row--hidden', p!=='sub'); }
    var micro = content.querySelector('.ft-microline'); if(micro){ micro.outerHTML = switchLineHTML(totals(model())); }
    repaint();
    if(page) repaintPage();
  }

  function open(){
    drawer.classList.add('drawer--open'); drawer.setAttribute('aria-hidden','false');
    if(back) back.classList.add('drawer-back--open'); document.body.style.overflow='hidden';
    // Multi-tab / long-idle freshness: re-pull truth when nothing local is pending.
    if(!engine.syncing && !engine.hasPending() && Date.now()-engine.lastSyncAt > 15000){ engine.refresh(); }
  }
  function close(){ drawer.classList.remove('drawer--open'); drawer.setAttribute('aria-hidden','true'); if(back) back.classList.remove('drawer-back--open'); document.body.style.overflow=''; }

  // Checkout drains the write queue first so the last click always reaches the
  // server before navigation. The button shows a busy state while draining; if
  // the network is wedged past the timeout we navigate anyway (server holds the
  // last successfully-synced cart).
  function trackBeginCheckout(){
    var m = model(), t = totals(m);
    var items = [];
    ['vanilla','chocolate','metab','bloat','bundle'].forEach(function(a){
      if(m[a].q > 0){ items.push(dlItem(a, m[a].q, dlUnit(a, m, t))); }
    });
    // Aliases with no DL name (the Vanilla bundle) were never part of this payload —
    // they used to be foreign lines — so their value stays out too: GA4 sees exactly
    // what it saw before. Including them is a deliberate, preview-tested change for later.
    var untracked = t.ups.reduce(function(s,u){ return s + (DL_NAMES[u.id] ? 0 : u.unit*u.q); }, 0);
    track('begin_checkout', t.grandSub - untracked, items);
  }
  function checkout(btn){
    // $0 fence: freebies can't check out alone (mirrors stripOrphanFreebies, which
    // is likely already sweeping them) — tell the shopper why instead of navigating.
    // Funnel (_bundle) carts are exempt: their anchor may be voucher-discounted or
    // $0-priced-pay-shipping by design, and they carry their own protection.
    if(blocksCheckout(model())){
      if(btn){
        btn.disabled = true;
        btn.innerHTML = 'Bonuses ship with a paid order';
        setTimeout(function(){ btn.disabled = false; render(); if(page) renderPage(); }, 2400);
      }
      return;
    }
    trackBeginCheckout();
    if(!engine.hasPending() && !engine.syncing){ window.location.href='/checkout'; return; }
    if(btn){ btn.disabled = true; btn.innerHTML = ICN.lock+'One moment…'; }
    engine.drain(8000).then(function(){ window.location.href='/checkout'; });
  }

  function refOf(node){
    var a = node.getAttribute('data-alias'); if(a) return {alias:a};
    var k = node.getAttribute('data-key'); if(k) return {key:k};
    return null;
  }
  function qtyOf(ref){
    if(!ref) return 0;
    if(ref.alias) return engine.displayQty(ref.alias);
    var o = engine.otherLines().filter(function(x){ return x.line.key===ref.key; })[0];
    return o ? o.q : 0;
  }
  function setQty(ref, q){
    if(!ref) return;
    if(ref.alias){ engine.setVar(ref.alias, q); }
    else { engine.setOther(ref.key, q); }
  }

  // event delegation — the same handlers drive the drawer and (when present) the cart
  // page. They live in separate DOM subtrees, so a click only fires for the surface it
  // happened in; the qty/plan paths repaint both surfaces via the engine + setPlan.
  // A tap on an add target — notably the big upsell card — can drift a few px,
  // a near-scroll the user never meant to make. The browser then swallows the
  // synthetic click, so the :hover checkmark flashes green but nothing is added.
  // We catch a low-movement pointerup on the add target and run the add directly;
  // addedByTapUntil tells the click handler it already happened so it can't double.
  var ADD_TAP_SLOP = 18;     // px of drift still counted as a tap (beyond → a scroll)
  var tapStart = null;       // {x,y,add} captured on pointerdown over an add target
  var addedByTapUntil = 0;   // Date.now() deadline for swallowing the trailing click

  function doAdd(add){
    var kind=add.getAttribute('data-cart-add');
    if((kind==='metab'||kind==='bloat'||kind==='bundle') && state.plan!=='sub'){ state.plan='sub'; engine.setAttr('plan','sub'); }
    state.freqManual = false;          // qty changed → auto-snap takes over again
    engine.bumpVar(kind, 1);
    trackQtyDelta(kind, 1);
  }
  function onCartPointerDown(e){
    if(e.pointerType === 'mouse'){ tapStart = null; return; }   // mouse keeps the plain click path
    var add = e.target.closest('[data-cart-add]');
    tapStart = add ? { x:e.clientX, y:e.clientY, add:add } : null;
  }
  function onCartPointerUp(e){
    var t = tapStart; tapStart = null;
    if(!t || e.pointerType === 'mouse') return;
    var dx = e.clientX - t.x, dy = e.clientY - t.y;
    if((dx*dx + dy*dy) > (ADD_TAP_SLOP*ADD_TAP_SLOP)) return;    // moved too far → it was a scroll
    addedByTapUntil = Date.now() + 600;                         // swallow the click that may still come
    doAdd(t.add);
  }

  function onCartClick(e){
    var add=e.target.closest('[data-cart-add]'); if(add){
      if(Date.now() < addedByTapUntil){ addedByTapUntil = 0; return; }  // pointerup already added it
      doAdd(add);
      return;
    }
    // Bridge bundle = all-or-nothing: any remove / decrement wipes the whole cart.
    if(isBundleCart() && (e.target.closest('[data-cart-remove]') || e.target.closest('[data-cart-dec]'))){ destroyBundleCart(); return; }
    var inc=e.target.closest('[data-cart-inc]'); if(inc){ state.freqManual = false; var r1=refOf(inc); setQty(r1, qtyOf(r1)+1); if(r1) trackQtyDelta(r1.alias, 1); return; }
    var dec=e.target.closest('[data-cart-dec]'); if(dec){ state.freqManual = false; var r2=refOf(dec); setQty(r2, Math.max(0, qtyOf(r2)-1)); if(r2) trackQtyDelta(r2.alias, -1); return; }
    var rm=e.target.closest('[data-cart-remove]'); if(rm){ state.freqManual = false; var r3=refOf(rm); var pq=qtyOf(r3); setQty(r3, 0); if(r3) trackQtyDelta(r3.alias, -pq); return; }
    var pl=e.target.closest('[data-cart-plan]'); if(pl){ setPlan(pl.getAttribute('data-cart-plan')); return; }
    var co=e.target.closest('[data-cart-checkout]'); if(co){ checkout(co); return; }
    var cl=e.target.closest('[data-cart-close]'); if(cl){ close(); return; }
  }
  function onCartChange(e){ var f=e.target.closest('[data-cart-freq]'); if(f){ state.freq=Number(f.value); state.freqManual = true; engine.setAttr('delivery_weeks', String(state.freq)); } }
  content.addEventListener('click', onCartClick);
  content.addEventListener('change', onCartChange);
  content.addEventListener('pointerdown', onCartPointerDown, { passive:true });
  content.addEventListener('pointerup', onCartPointerUp, { passive:true });
  content.addEventListener('pointercancel', function(){ tapStart = null; }, { passive:true });
  if(page){
    page.addEventListener('click', onCartClick);
    page.addEventListener('change', onCartChange);
    page.addEventListener('pointerdown', onCartPointerDown, { passive:true });
    page.addEventListener('pointerup', onCartPointerUp, { passive:true });
    page.addEventListener('pointercancel', function(){ tapStart = null; }, { passive:true });
  }
  // Express buttons submit the SERVER cart immediately (no drain), so nudge a flush the moment
  // one is pressed — best-effort freshness for Shop Pay / Google Pay. The listener rides the
  // node wherever it's mounted.
  if(expressHome){ var xnode = expressHome.querySelector('[data-cart-express]'); if(xnode){ xnode.addEventListener('pointerdown', function(){ engine.flushSoon(0); }); } }
  if(back) back.addEventListener('click', close);
  document.addEventListener('keydown', function(e){ if(e.key==='Escape') close(); });
  document.addEventListener('click', function(e){ if(e.target.closest('[data-biome-cart]')){ e.preventDefault(); open(); } });

  // Don't lose the last click to navigation: replay outstanding writes with a
  // keepalive request when the page is being hidden/unloaded.
  window.addEventListener('pagehide', function(){ engine.flushKeepalive(); });
  document.addEventListener('visibilitychange', function(){ if(document.visibilityState==='hidden'){ engine.flushKeepalive(); } });
  // bfcache restore serves a stale DOM snapshot — re-pull the cart.
  window.addEventListener('pageshow', function(e){ if(e.persisted){ engine.refresh(); } });

  window.BiomeCart = {
    open: function(){ open(); },
    close: close,
    refresh: function(){ return engine.refresh(); },
    _engine: engine,   // test/debug handle — not a public API
    // PDP/buy-box entry point. Opens instantly with optimistic lines; resolves
    // once the cart has synced (callers use this to re-enable their button).
    // Known SKUs ride the absolute-qty engine; anything else falls back to a
    // serialized add.js. Plan/freq arrive as cart attributes.
    addAndOpen: function(items, opts){
      opts = opts || {};
      // freqManual: the buy-box says the shopper hand-picked this cadence —
      // honor it (auto-snap stands down until the next qty change). Without
      // the flag, qty is changing so the snap re-engages as usual.
      state.freqManual = !!opts.freqManual;
      if(opts.plan){ state.plan = opts.plan; engine.setAttr('plan', opts.plan); }
      if(opts.freq){ state.freq = Number(opts.freq) || state.freq; engine.setAttr('delivery_weeks', String(state.freq)); }
      var dlAdds = [];
      // Put every item in, THEN paint once. Each setVar() fires onChange, which used to
      // repaint — a full render() for a new line — per item, and the render() below ran on
      // top: 2 full renders for a one-item add, 3 for Chocolate + Vanilla, every one but the
      // last thrown away (it all runs before the browser paints). onChange's state work
      // still runs per item exactly as before; only its painting is deferred.
      paintBatch++; paintOwed = false;
      try {
        (items||[]).forEach(function(it){
          var alias = engine.aliasByVid(it.id);
          if(alias){ engine.setVar(alias, engine.displayQty(alias) + (it.quantity||1)); dlAdds.push({ alias: alias, qty: it.quantity||1 }); }
          else { engine.queueAdd(it); }
        });
      } finally { paintBatch--; }
      if(dlAdds.length){
        var m = model(), t = totals(m), dlItems = [], dlValue = 0;
        dlAdds.forEach(function(a){
          if(!DL_NAMES[a.alias]) return;
          var unit = dlUnit(a.alias, m, t);
          dlItems.push(dlItem(a.alias, a.qty, unit));
          dlValue += unit * a.qty;
        });
        track('add_to_cart', dlValue, dlItems);
      }
      render();                                     // the one paint for the whole add
      if(page && paintOwed) repaintPage();          // the cart-page update onChange deferred
      paintOwed = false;
      open();
      return engine.drain(6000);
    }
  };

  // Seed from the server-rendered cart so the FIRST paint already shows the real line
  // items — no empty→filled flash on the cart page (e.g. returning from checkout). The
  // refresh() below reconciles against the live AJAX cart a beat later.
  if(cfg.serverCart && cfg.serverCart.items){
    engine.cart = cfg.serverCart;
    hydrateFromAttrs();   // adopt persisted plan/cadence so the first paint is correct too
    hydrated = true;      // don't re-run hydration on the first reconcile (respect later manual picks)
  }
  render();           // paint immediately — seeded cart if present, else empty
  if(page) renderPage();
  engine.refresh().then(function(c){
    if(!c){ setTimeout(function(){ engine.refresh(); }, 800); }
  });
})();
